import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import getApi from './api/getApi';
import postApi from './api/postApi';
import { BASEURL } from './constant';
import FlashMessage, { FlashStatus } from './components/FlashMessage';
import MyDataTable from './components/DataTable';
import CircularProgress from './components/CircularProgress';

interface City {
  value: string;
  label: string;
  id: number;
}

interface Stadium {
  value: string;
  label: string;
  id: number;
}

interface Match {
  id: number;
  City: string;
  Stadium: string;
  Round: string;
  'Match Date': string;
  Status: string;
}

export default function FIFA26() {
  const [cities, setCities] = useState<City[]>([]);
  const [stadiums, setStadiums] = useState<Stadium[]>([]);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [selectedStadium, setSelectedStadium] = useState<Stadium | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [showProgress, setShowProgress] = useState(false);
  const [flashContent, setFlashContent] = useState<{ message: string; status?: FlashStatus }>({ message: '' });

  const role = localStorage.getItem('role');
  const userName = localStorage.getItem('userName');
  const userEmail = localStorage.getItem('email');
  
  // Debug: Log the current user role
  console.log('FIFA26 - Current user role:', role);

  // Load cities on component mount
  useEffect(() => {
    const loadCities = async () => {
      setShowProgress(true);
      try {
        const response = await getApi(`${BASEURL}/fifa26/cities`);
        if (response && response.data) {
          const cityOptions: City[] = response.data.map((city: any) => ({
            value: city.city_name,
            label: city.city_name,
            id: city.id
          }));
          setCities(cityOptions);
        } else {
          setFlashContent({ message: 'Failed to load cities', status: 'danger' });
        }
      } catch (error) {
        setFlashContent({ message: 'Error loading cities', status: 'danger' });
      }
      setShowProgress(false);
    };

    loadCities();
  }, []);

  // Load stadiums when city is selected
  const handleCityChange = async (city: City | null) => {
    setSelectedCity(city);
    setSelectedStadium(null);
    setMatches([]);
    setStadiums([]);

    if (city) {
      setShowProgress(true);
      try {
        const response = await getApi(`${BASEURL}/fifa26/stadiums/${encodeURIComponent(city.value)}`);
        if (response && response.data) {
          const stadiumOptions: Stadium[] = response.data.map((stadium: any) => ({
            value: stadium.stadium_name,
            label: stadium.stadium_name,
            id: stadium.id
          }));
          setStadiums(stadiumOptions);
        } else {
          setFlashContent({ message: 'No stadiums found for this city', status: 'warning' });
        }
      } catch (error) {
        setFlashContent({ message: 'Error loading stadiums', status: 'danger' });
      }
      setShowProgress(false);
    }
  };

  // Load matches when stadium is selected
  const handleStadiumChange = async (stadium: Stadium | null) => {
    setSelectedStadium(stadium);
    setMatches([]);

    if (stadium) {
      setShowProgress(true);
      try {
        const response = await getApi(`${BASEURL}/fifa26/matches?city=${encodeURIComponent(selectedCity?.value || '')}&stadium=${encodeURIComponent(stadium.value)}&show_all=true`);
        if (response && response.data) {
          setMatches(response.data);
        } else {
          setFlashContent({ message: 'No matches found for this stadium', status: 'warning' });
        }
      } catch (error) {
        setFlashContent({ message: 'Error loading matches', status: 'danger' });
      }
      setShowProgress(false);
    }
  };

  // Subscribe to a match
  const subscribeToMatch = async (match: Match) => {
    if (role !== 'customer') {
      setFlashContent({ message: 'Only customers can subscribe to matches', status: 'warning' });
      return;
    }

    setShowProgress(true);
    try {
      const subscriptionData = {
        id: match.id,
        name: userName,
        email: userEmail,
        message: `Following customer in the FIFA26 market would like to subscribe for the ${match.Round} match on: ${match['Match Date']} at ${match.Stadium}, ${match.City}`
      };

      const response = await postApi(`${BASEURL}/fifa26/subscribe`, subscriptionData);
      if (response.status >= 200 && response.status < 300) {
        setFlashContent({ message: response.data.message || 'Successfully subscribed to match!', status: 'success' });
        // Refresh matches to show updated status
        if (selectedStadium && selectedCity) {
          await handleStadiumChange(selectedStadium);
        }
      } else {
        setFlashContent({ message: response.data.message || 'Subscription failed', status: 'danger' });
      }
    } catch (error) {
      setFlashContent({ message: 'Error subscribing to match', status: 'danger' });
    }
    setShowProgress(false);
  };

  // Create table columns
  const createColumns = () => {
    const columns: any[] = [
      {
        name: 'City',
        selector: (row: Match) => row.City,
        sortable: true,
      },
      {
        name: 'Stadium',
        selector: (row: Match) => row.Stadium,
        sortable: true,
      },
      {
        name: 'Round',
        selector: (row: Match) => row.Round,
        sortable: true,
      },
      {
        name: 'Match Date',
        selector: (row: Match) => row['Match Date'],
        sortable: true,
      }
    ];

    // Add Subscribe column for customers, Status column for admin/engineer
    if (role === 'customer') {
      columns.push({
        name: 'Subscribe',
        selector: (row: Match) => 'Subscribe',
        sortable: false,
        cell: (row: Match) => {
          const status = row.Status;
          const isSubscribed = status === "Subscribed";
          
          return (
            <div className="fifa26-subscribe-row">
              <button
                className={`fifa26-subscribe-button ${isSubscribed ? "btn-success" : "btn-primary"}`}
                disabled={isSubscribed}
                onClick={() => !isSubscribed && subscribeToMatch(row)}
              >
                {isSubscribed ? "Subscribed" : "Subscribe"}
              </button>
            </div>
          );
        },
      });
    } else {
      // Admin and Engineer users see Status column (read-only)
      columns.push({
        name: 'Status',
        selector: (row: Match) => row.Status,
        sortable: true,
      });
    }

    return columns;
  };

  return (
    <div className="fifa26-container">
      {showProgress && <CircularProgress />}
      
      {flashContent.message && flashContent.status && (
        <FlashMessage
          message={flashContent.message}
          duration={3000}
          status={flashContent.status}
          onClose={() => setFlashContent({ message: '', status: undefined })}
        />
      )}

      <div className="row mb-2">
        <div className="col-12">
          <div className="fifa26-banner-container">
            <img 
              src="/fifa_2026_banner_centered.png" 
              alt="FIFA World Cup 2026" 
              className="fifa26-banner-image"
              onError={(e) => {
                // Fallback to text if image doesn't exist
                e.currentTarget.style.display = 'none';
                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextElement) {
                  nextElement.style.display = 'block';
                }
              }}
            />
            <div className="fifa26-fallback-text">
              <h2>
                🏆 FIFA World Cup 2026 - Match Monitoring Subscription
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="row fifa26-dropdown-row">
        <div className="col-md-6">
          <label className="fifa26-dropdown-label">
            Select Host City
          </label>
          <Select
            options={cities}
            value={selectedCity}
            onChange={handleCityChange}
            placeholder="Choose a FIFA 2026 host city..."
            isClearable
            isSearchable
            className="fifa26-select"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '45px',
                fontSize: '16px'
              })
            }}
          />
        </div>

        <div className="col-md-6">
          <label className="fifa26-dropdown-label">
            Select Stadium
          </label>
          <Select
            options={stadiums}
            value={selectedStadium}
            onChange={handleStadiumChange}
            placeholder={selectedCity ? "Choose a stadium..." : "Select a city first"}
            isClearable
            isSearchable
            isDisabled={!selectedCity}
            className="fifa26-select"
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '45px',
                fontSize: '16px'
              })
            }}
          />
        </div>
      </div>

      {selectedCity && selectedStadium && (
        <div className="row">
          <div className="col-12">
            <div className="fifa26-matches-container">
              <h4 className="fifa26-matches-title">
                📅 Available Matches at {selectedStadium.label}, {selectedCity.value}
              </h4>
              
              {matches.length > 0 ? (
                <MyDataTable
                  columns={createColumns()}
                  rows={matches}
                  title=""
                />
              ) : (
                <div className="fifa26-no-matches">
                  🎉 No matches available for subscription at this stadium.
                  <small>All matches may have been subscribed or scheduled.</small>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!selectedCity && (
        <div className="fifa26-welcome-message">
          ⚽ Select a host city to begin exploring FIFA 2026 World Cup matches
        </div>
      )}
    </div>
  );
}
